import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { GitMerge, Loader2, X } from 'lucide-react';

import {
  ttqService,
  type TtqSession,
  type TtqSessionMergeAssessment,
} from '../../services/ttqService';
import { resolveTtqSessionTitle } from '../../utils/ttqDisplay';

type Props = {
  open: boolean;
  /** 当前品题 = 合并主会话（被保留侧） */
  primarySession: TtqSession;
  peerSessions: TtqSession[];
  onClose: () => void;
  onMerged: (session: TtqSession) => void;
};

type Step = 'pick' | 'reason' | 'result';

/**
 * Web：将其他品题合并到当前品题（对齐 Mobile2 TtqSessionMergeSheet）。
 * primary 固定为当前会话；用户只选 secondary。
 */
export function TtqSessionMergeDialog({
  open,
  primarySession,
  peerSessions,
  onClose,
  onMerged,
}: Props) {
  const [step, setStep] = useState<Step>('pick');
  const [secondaryId, setSecondaryId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [assessment, setAssessment] = useState<TtqSessionMergeAssessment | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidates = useMemo(
    () => peerSessions.filter((item) => item.id !== primarySession.id),
    [peerSessions, primarySession.id],
  );

  const secondary = useMemo(
    () => candidates.find((item) => item.id === secondaryId) || null,
    [candidates, secondaryId],
  );

  useEffect(() => {
    if (!open) {
      setStep('pick');
      setSecondaryId(null);
      setReason('');
      setAssessment(null);
      setBusy(false);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const handleAssess = async () => {
    if (!secondaryId) {
      setError('请选择要并入当前品题的另一场品题');
      return;
    }
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('请填写合并理由');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await ttqService.assessSessionMerge({
        primarySessionId: primarySession.id,
        secondarySessionId: secondaryId,
        reason: trimmed,
      });
      setAssessment(result);
      setStep('result');
    } catch (assessError) {
      setError(assessError instanceof Error ? assessError.message : '合并评估失败');
    } finally {
      setBusy(false);
    }
  };

  const runMerge = async (mode: 'force' | 'accept_ai') => {
    if (!secondaryId) return;
    const trimmed = reason.trim();
    if (!trimmed) return;
    if (!window.confirm('合并不可撤销：另一场品题将并入当前品题。确定继续？')) return;

    setBusy(true);
    setError(null);
    try {
      const merged = await ttqService.mergeSessions({
        primarySessionId: primarySession.id,
        secondarySessionId: secondaryId,
        reason: trimmed,
        mode,
        assessmentId: assessment?.assessmentId,
        assessment: assessment || undefined,
      });
      onMerged(merged);
      onClose();
    } catch (mergeError) {
      setError(mergeError instanceof Error ? mergeError.message : '合并失败');
    } finally {
      setBusy(false);
    }
  };

  const canAcceptAi = assessment?.suggestedAction === 'merge';

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 px-4">
      <div className="flex max-h-[min(88vh,36rem)] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-ink">
              <GitMerge size={16} className="shrink-0 text-primary" />
              合并品题到当前
            </p>
            <p className="mt-0.5 text-xs text-muted">
              其他品题并入「{resolveTtqSessionTitle(primarySession)}」，画布与品题浓缩结果会一并迁入。
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-black/[0.05] hover:text-ink disabled:opacity-50"
            aria-label="关闭"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {error ? <p className="mb-2 text-xs text-danger">{error}</p> : null}

          {step === 'pick' ? (
            <div className="space-y-2">
              <p className="text-[11px] text-muted">选择要并入当前品题的另一场品题：</p>
              {candidates.length === 0 ? (
                <p className="rounded-md border border-border bg-canvas/50 px-3 py-4 text-xs text-muted">
                  暂无其他可合并的品题会话。
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {candidates.map((item) => {
                    const selected = item.id === secondaryId;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          disabled={busy}
                          className={
                            'flex w-full flex-col rounded-md border px-3 py-2 text-left text-sm disabled:opacity-50 '
                            + (selected
                              ? 'border-primary/50 bg-primary/5 text-ink'
                              : 'border-border bg-canvas/40 text-ink hover:bg-black/[0.03]')
                          }
                          onClick={() => {
                            setError(null);
                            setAssessment(null);
                            setSecondaryId(item.id);
                          }}
                        >
                          <span className="truncate font-medium">
                            {resolveTtqSessionTitle(item)}
                          </span>
                          <span className="mt-0.5 text-[10px] text-muted">
                            {(item.particles || []).length} 颗粒
                            {selected ? ' · 将并入当前' : ''}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}

          {step === 'reason' ? (
            <div className="space-y-2">
              <p className="text-[11px] text-muted">
                并入：{secondary ? resolveTtqSessionTitle(secondary) : '（未选）'}
                {' → '}
                当前：{resolveTtqSessionTitle(primarySession)}
              </p>
              <label className="block text-[11px] font-medium text-ink">
                合并理由
                <textarea
                  value={reason}
                  disabled={busy}
                  rows={4}
                  className="mt-1 w-full resize-none rounded-md border border-border bg-canvas px-2.5 py-2 text-sm text-ink outline-none focus:border-primary/50"
                  placeholder="说明为何将两场品题合并…"
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
            </div>
          ) : null}

          {step === 'result' && assessment ? (
            <div className="space-y-2 text-sm">
              <p className="text-xs text-muted">
                评估分 {assessment.score}
                {assessment.hasMergeValue ? ' · 建议可合并' : ' · 建议暂分置'}
              </p>
              <p className="leading-relaxed text-ink">{assessment.rationale}</p>
              {assessment.risks?.length ? (
                <ul className="list-disc space-y-0.5 pl-4 text-xs text-muted">
                  {assessment.risks.map((risk) => (
                    <li key={risk}>{risk}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-4 py-3">
          {step === 'pick' ? (
            <>
              <button
                type="button"
                disabled={busy}
                className="rounded-md border border-border px-3 py-1.5 text-xs text-ink hover:bg-black/[0.03] disabled:opacity-50"
                onClick={onClose}
              >
                取消
              </button>
              <button
                type="button"
                disabled={busy || !secondaryId}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                onClick={() => {
                  setError(null);
                  setStep('reason');
                }}
              >
                下一步
              </button>
            </>
          ) : null}

          {step === 'reason' ? (
            <>
              <button
                type="button"
                disabled={busy}
                className="rounded-md border border-border px-3 py-1.5 text-xs text-ink hover:bg-black/[0.03] disabled:opacity-50"
                onClick={() => setStep('pick')}
              >
                上一步
              </button>
              <button
                type="button"
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                onClick={() => void handleAssess()}
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                评估合并
              </button>
            </>
          ) : null}

          {step === 'result' ? (
            <>
              <button
                type="button"
                disabled={busy}
                className="rounded-md border border-border px-3 py-1.5 text-xs text-ink hover:bg-black/[0.03] disabled:opacity-50"
                onClick={() => setStep('reason')}
              >
                返回
              </button>
              <button
                type="button"
                disabled={busy}
                className="rounded-md border border-border px-3 py-1.5 text-xs text-ink hover:bg-black/[0.03] disabled:opacity-50"
                onClick={() => void runMerge('force')}
              >
                强制合并
              </button>
              <button
                type="button"
                disabled={busy || !canAcceptAi}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                onClick={() => void runMerge('accept_ai')}
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                按建议合并
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
